import { entityLikeSchema } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import type { DealsGroupFindInterface } from '@zaoblako/shared/modules/business/dto/deals-group/deals-group-find.dto';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import type { DealStatusFindInterface } from '@zaoblako/shared/modules/business/dto/deal-status/deal-status-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import type { ProjectFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import { DealOrderFormDto, type DealOrderFormInterface } from '@zaoblako/shared/modules/business/dto/deal-order/deal-order-form.dto';
import { HelpdeskTaskPriorityEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.priority.enum';
import { HelpdeskPriorityI18nEnum } from '@zaoblako/shared/modules/helpdesk/i18n/helpdesk.priority.i18n.enum';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { BusinessDtoKeysEnum } from '@zaoblako/shared/modules/business/enum/business-dto-keys.enum';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';

import * as yup from 'yup';

/** Deal creation and updating requests body */
export interface DealFormInterface {
  /** Deal display name */
  name: string;
  /** Deal shipping date and time */
  shippingDate?: Date;
  /** Project priority ({@link HelpdeskTaskPriorityEnum.MEDIUM} by default) */
  priority?: HelpdeskTaskPriorityEnum;
  plannedMin?: number;
  status?: DealStatusFindInterface;
  /** Deal commentary (just some text, maybe formatted) */
  comment?: string;
  /** Deal's counterparty */
  contragent: ContragentFindInterface;
  /** Deal's company */
  company?: CompanyFindInterface;
  /** Deal's direction */
  direction?: DirectionFindInterface;
  /** Deal's unit */
  unit?: UnitFindInterface;
  /** Deal's filial */
  filial?: FilialFindInterface;
  /** Group this deal belongs to (optional) */
  group?: DealsGroupFindInterface;
  /** User auditors (deals group participants) */
  userAuditors?: UserFindInterface[];
  /** Group auditors (deals group participants) */
  groupAuditors?: UserGroupFindInterface[];
  createdBy?: UserFindInterface;
  responsible?: UserFindInterface;
  orders: DealOrderFormInterface[];
  /** Projects to associate with deal */
  projects?: ProjectFindInterface[];
  /** HelpDesk tasks to associate with deal */
  helpdeskTasks?: HelpdeskTaskFindInterface[];
}

/** Deal creation and updating requests body - DTO schema */
export const DealFormDto = SerdeDtoClass.create<DealFormInterface>(BusinessDtoKeysEnum.DEAL_FORM_DTO, {
  name: {
    label: 'Название',
    serializer: yup.string(),
  },
  shippingDate: {
    optional: true,
    label: 'Дата',
    serializer: yup.date(),
  },
  priority: {
    optional: true,
    label: 'Приоритет',
    serializer: commonEnumValidator(HelpdeskTaskPriorityEnum, HelpdeskPriorityI18nEnum),
  },
  plannedMin: {
    optional: true,
    label: 'Запланированное время',
    serializer: yup
      .number()
      .integer(DtoMessageEnum.NOT_INTEGER)
      .min(0, 'Запланированное время в минутах не может быть отрицательным'),
  },
  status: {
    optional: true,
    label: 'Статус',
    serializer: entityLikeSchema,
  },
  comment: {
    optional: true,
    label: 'Комментарий',
    serializer: yup.string(),
  },
  contragent: {
    optional: true,
    label: 'Контрагент',
    serializer: entityLikeSchema,
  },
  company: {
    optional: true,
    label: 'Компания',
    serializer: entityLikeSchema,
  },
  direction: {
    optional: true,
    label: 'БС',
    serializer: entityLikeSchema,
  },
  unit: {
    optional: true,
    label: 'БУ',
    serializer: entityLikeSchema,
  },
  filial: {
    optional: true,
    label: 'Филиал',
    serializer: entityLikeSchema,
  },
  group: {
    optional: true,
    label: 'Группа',
    serializer: entityLikeSchema,
  },
  userAuditors: {
    optional: true,
    label: 'Аудиторы',
    serializer: yup.array(entityLikeSchema),
  },
  groupAuditors: {
    optional: true,
    label: 'Группы аудиторов',
    serializer: yup.array(entityLikeSchema),
  },
  createdBy: {
    optional: true,
    label: 'Создатель',
    serializer: entityLikeSchema,
  },
  responsible: {
    optional: true,
    label: 'Ответственный',
    serializer: entityLikeSchema,
  },
  orders: {
    label: 'Заказы',
    nested: DealOrderFormDto,
    serializer: (_, nested) => yup.array(nested),
  },
  projects: {
    optional: true,
    label: 'Проекты',
    serializer: yup.array(entityLikeSchema),
  },
  helpdeskTasks: {
    optional: true,
    label: 'Задачи HD',
    serializer: yup.array(entityLikeSchema),
  },
});
